import React, { useState } from 'react';
import InlineMessaging, {
  InlineMessagingTheme,
} from '@havenengineering/module-shared-library/dist/components/InlineMessaging/InlineMessaging';
import { Button } from '@havenengineering/module-shared-owners-ui/dist/components/Button';
import { useAuthContext } from '@havenengineering/module-shared-owners-ui/dist/contexts/auth';

import { useGuestList } from '../../hooks/useGuestList';
import { CounterButtons } from '../CounterButtons/CounterButtons';
import { upsertParticipants } from '../helpers/bookings';
import { useBooking } from './bookingContext';
import styles from './ParticipantEditor.module.scss';
import ParticipantForm from './ParticipantForm';

const ParticipantEditor = () => {
  const { activeAccount } = useAuthContext();
  const { booking, handleUpsertParticipants } = useBooking();

  const [editingRowKey, setEditingRowKey] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const { rows, guests, addGuest, updateGuest, removeGuest, replaceGuests } =
    useGuestList(booking.users);
  const [childNumber, setChildNumber] = useState(booking.childNumber);
  const [toddlersNumber, setToddlersNumber] = useState(booking.toddlersNumber);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSave = async () => {
    setError('');
    setSuccess(false);
    setLoading(true);
    const body = {
      additionalGuests: guests.map(({ id, name, phoneNumber }) => ({
        id,
        name,
        phoneNumber,
      })),
      childNumber,
      toddlersNumber,
    };
    try {
      const saved = await upsertParticipants(body, booking, activeAccount);
      handleUpsertParticipants(saved);
      replaceGuests(saved.participants);
      setChildNumber(saved.childNumber);
      setToddlersNumber(saved.toddlersNumber);
      setSuccess(true);
    } catch (error) {
      setError('Something went wrong, please try again');
    } finally {
      setLoading(false);
    }
  };

  const handleParticipantSave = (participant: Participant) => {
    addGuest(participant);
    setShowForm(false);
  };

  const handleParticipantEdit = (rowKey: string, participant: Participant) => {
    updateGuest(rowKey, participant);
    setEditingRowKey(null);
  };

  const handleCancelEdit = () => {
    setEditingRowKey(null);
    setShowForm(false);
  };

  const rowBeingEdited = rows.find(({ rowKey }) => rowKey === editingRowKey);

  return (
    <div className={styles.editorContainer} data-testid="participants-editor">
      <div className={styles.counter}>
        <p>
          <strong>Adults</strong>
          <br />
          <small>16+ years</small>
        </p>
        <Button
          className={styles.iconButton}
          variant="outlined"
          onClick={() => setShowForm(true)}
          type="button"
          disabled={loading || !!rowBeingEdited}
          data-testid="add-adult-btn"
        >
          <img
            src="https://www.haven.com/assets/icons/plus.svg"
            width={24}
            height={24}
            alt="add"
          />
        </Button>
      </div>
      {rows.map(({ rowKey, guest }, idx) => (
        <div key={rowKey} className={styles.userRow}>
          <p>
            <strong>{guest.name}</strong>
            <br />
            <small>{guest.phoneNumber}</small>
          </p>
          <div>
            <Button
              className={styles.iconButton}
              variant="outlined"
              disabled={loading || showForm}
              onClick={() => removeGuest(rowKey)}
              type="button"
              data-testid={`delete-${idx}`}
            >
              <img
                src="/assets/icon-delete.svg"
                width={24}
                height={24}
                alt="add"
              />
            </Button>
            <Button
              className={styles.iconButton}
              variant="outlined"
              disabled={loading || showForm}
              onClick={() => setEditingRowKey(rowKey)}
              type="button"
              data-testid={`edit-${idx}`}
            >
              <img
                src="https://www.haven.com/assets/icons/pencil-blue.svg"
                width={24}
                height={24}
                alt="add"
              />
            </Button>
          </div>
        </div>
      ))}
      {rowBeingEdited && (
        <ParticipantForm
          participant={rowBeingEdited.guest}
          handleSaveParticipant={(participant) =>
            handleParticipantEdit(rowBeingEdited.rowKey, participant)
          }
          handleCancel={() => handleCancelEdit()}
        />
      )}
      {showForm && (
        <ParticipantForm
          handleSaveParticipant={handleParticipantSave}
          handleCancel={() => handleCancelEdit()}
        />
      )}

      <hr className={styles.divider} />
      <InlineMessaging
        type={InlineMessagingTheme.INFO_THEME}
        title="Info"
        message="We do not require any guest details for under 16 year olds"
        dismissible={false}
      />
      <div className={styles.counter}>
        <p>
          <strong>Children</strong>
          <br />
          <small>4 - 16 year olds</small>
        </p>
        <CounterButtons
          count={childNumber}
          onDecrement={(count) => setChildNumber(count)}
          onIncrement={(count) => setChildNumber(count)}
          testId="children"
          disabled={loading}
        />
      </div>

      <hr className={styles.divider} />
      <div className={styles.counter}>
        <p>
          <strong>Infants</strong>
          <br />
          <small>0 - 4 year olds</small>
        </p>
        <CounterButtons
          count={toddlersNumber}
          onDecrement={(count) => setToddlersNumber(count)}
          onIncrement={(count) => setToddlersNumber(count)}
          testId="toddlers"
          disabled={loading}
        />
      </div>

      {error && (
        <InlineMessaging
          type={InlineMessagingTheme.ERROR_THEME}
          title="Error"
          message={error}
          dismissible={false}
        />
      )}

      <Button
        fullWidth
        className={styles.saveButton}
        isLoading={loading}
        disabled={showForm || !!rowBeingEdited}
        type="submit"
        onClick={() => handleSave()}
      >
        Save Participants
      </Button>
      {success && (
        <InlineMessaging
          type={InlineMessagingTheme.SUCCESS_THEME}
          title="Success"
          message="Update was successful"
          dismissible={false}
        />
      )}
    </div>
  );
};

export default ParticipantEditor;
